<script>
import { format, fromUnixTime } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Message from './Message.vue';

export default {
  name: 'ActivityMessageGroup',
  components: {
    Message,
  },
  props: {
    messages: {
      type: Array,
      required: true,
    },
    isATweet: {
      type: Boolean,
      default: false,
    },
    isAWhatsAppChannel: {
      type: Boolean,
      default: false,
    },
    isWebWidgetInbox: {
      type: Boolean,
      default: false,
    },
    isAFacebookInbox: {
      type: Boolean,
      default: false,
    },
    isAnEmailInbox: {
      type: Boolean,
      default: false,
    },
    isInstagram: {
      type: Boolean,
      default: false,
    },
    inboxSupportsReplyTo: {
      type: Object,
      default: () => ({ incoming: false, outgoing: false }),
    },
  },
  data() {
    return {
      isCollapsed: true,
    };
  },
  computed: {
    userName() {
      const firstMessage = this.messages[0];

      // Try to get from meta_data first (new activity messages)
      if (firstMessage?.meta_data?.performed_by?.name) {
        return firstMessage.meta_data.performed_by.name;
      }

      // Fallback to sender for backward compatibility
      if (firstMessage?.sender?.name) {
        return firstMessage.sender.name;
      }

      return 'Sistema';
    },
    actionCount() {
      return this.messages.length;
    },
    formattedTime() {
      const firstMessage = this.messages[0];
      if (!firstMessage?.created_at) return '';

      const timestamp = fromUnixTime(firstMessage.created_at);
      return format(timestamp, "d 'de' MMM, HH:mm", { locale: ptBR });
    },
    collapsedText() {
      const acoes = this.actionCount === 1 ? 'ação' : 'ações';
      return `${this.userName} realizou ${this.actionCount} ${acoes} ${this.formattedTime}`;
    },
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    getInReplyToMessage(message) {
      // Activity messages typically don't have reply-to, but keeping for consistency
      return {};
    },
  },
};
</script>

<template>
  <li class="activity-message-group center">
    <div
      class="activity-group-header"
      :class="{ 'is-expanded': !isCollapsed }"
      @click="toggleCollapse"
    >
      <span class="activity-group-text">{{ collapsedText }}</span>
      <fluent-icon
        :icon="isCollapsed ? 'chevron-down' : 'chevron-up'"
        size="16"
        class="chevron-icon"
      />
    </div>

    <transition name="expand">
      <div v-show="!isCollapsed" class="activity-group-expanded">
        <Message
          v-for="message in messages"
          :key="message.id"
          class="grouped-activity-message"
          :data="message"
          :is-a-tweet="isATweet"
          :is-a-whatsapp-channel="isAWhatsAppChannel"
          :is-web-widget-inbox="isWebWidgetInbox"
          :is-a-facebook-inbox="isAFacebookInbox"
          :is-an-email-inbox="isAnEmailInbox"
          :is-instagram="isInstagram"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :in-reply-to="getInReplyToMessage(message)"
        />
      </div>
    </transition>
  </li>
</template>

<style scoped lang="scss">
.activity-message-group {
  @apply flex flex-col items-center justify-center my-1;

  &.center {
    @apply items-center justify-center;
  }
}

.activity-group-header {
  @apply flex items-center justify-center gap-2
    text-sm text-slate-500 dark:text-slate-400
    cursor-pointer px-2 py-1 rounded-md
    transition-colors duration-200;

  &:hover {
    @apply bg-slate-50 dark:bg-slate-800;
  }

  &.is-expanded {
    @apply mb-2;
  }
}

.activity-group-text {
  @apply select-none;
}

.chevron-icon {
  @apply transition-transform duration-200 flex-shrink-0;
}

.activity-group-expanded {
  @apply flex flex-col w-full;
}

.grouped-activity-message {
  @apply my-0.5;
}

// Transition for expand/collapse
.expand-enter-active,
.expand-leave-active {
  @apply transition-all duration-200 ease-in-out;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  @apply opacity-0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  @apply opacity-100;
  max-height: 2000px;
}
</style>
